// src/pages/admin/Subscribed.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, MailCheck } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const Subscribed = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/subscribe`);
      setSubscribers(res.data);
    } catch (err) {
      setError("We're having trouble loading subscribers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to remove this subscriber?",
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${SERVER_URL}/api/subscribe/${id}`);
      setSubscribers((prev) => prev.filter((s) => s._id !== id));
    } catch (err) {
      alert("Couldn't remove subscriber. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">
          Subscribed Users
        </h1>
        <p className="text-gray-500 mt-1">
          Manage your newsletter subscriber list
        </p>
      </div>

      <div className="bg-white border border-gray-100 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.02)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <CircularProgress size={35} sx={{ color: "#eab308" }} />
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <p className="bg-red-500/5 border border-red-500/10 text-red-600 px-6 py-4 rounded-2xl inline-block text-sm font-semibold">
              {error}
            </p>
          </div>
        ) : subscribers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-2">
              <MailCheck className="w-8 h-8 text-gray-200" />
            </div>
            <p className="text-gray-400 font-bold text-sm tracking-tight">
              No active subscribers
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-50">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.15em]">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.15em]">
                    Subscriber Details
                  </th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.15em]">
                    Enrolled On
                  </th>
                  <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.15em]">
                    Administrative
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {subscribers.map((sub, idx) => (
                  <tr
                    key={sub._id}
                    className="hover:bg-yellow-50/30 transition-all group"
                  >
                    <td className="px-6 py-3 text-[11px] font-black text-gray-300">
                      #{idx + 1}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center text-yellow-500 text-[10px] font-black shadow-sm group-hover:from-yellow-500 group-hover:to-yellow-600 group-hover:text-white transition-all duration-300">
                          {sub.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-900 group-hover:text-yellow-600 transition-colors">
                            {sub.email}
                          </span>
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-tighter">
                            Verified Member
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-600">
                          {new Date(sub.subscribedAt).toLocaleDateString(
                            undefined,
                            { day: "numeric", month: "short", year: "numeric" },
                          )}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium">
                          {new Date(sub.subscribedAt).toLocaleTimeString(
                            undefined,
                            { hour: "2-digit", minute: "2-digit" },
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => handleDelete(sub._id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all text-xs font-black uppercase tracking-tighter"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Exclude
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-50 flex items-center justify-between text-[11px] font-bold">
              <span className="text-gray-400 uppercase tracking-widest">
                Subscriber Inventory
              </span>
              <span className="text-yellow-600 bg-yellow-100/50 px-3 py-1 rounded-full px-2">
                {subscribers.length} Global Entries
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscribed;
